import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { crmService } from '../../services/crmService';
import { TrackRecord, PublisherCommunication, Publisher, SendPublisherEmailRequest } from '../../types/crm';

const Container = styled.div`
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
`;

const Header = styled.div`
    margin-bottom: 2rem;
`;

const Title = styled.h1`
    font-size: 1.75rem;
    color: #111827;
    margin-bottom: 0.5rem;
`;

const BackButton = styled.button`
    padding: 0.5rem 1rem;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: 1rem;
    &:hover {
        background: #f3f4f6;
    }
`;

const Section = styled.div`
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.25rem;
    color: #374151;
    margin-bottom: 1rem;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 0.5rem;
`;

const PublisherInfo = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
`;

const InfoItem = styled.div`
    label {
        display: block;
        font-size: 0.875rem;
        color: #6b7280;
        margin-bottom: 0.25rem;
    }
    div {
        font-size: 1rem;
        color: #111827;
        font-weight: 500;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
`;

const Th = styled.th`
    text-align: left;
    padding: 0.75rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
`;

const Td = styled.td`
    padding: 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    color: #1f2937;
    font-size: 0.875rem;
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.875rem;
    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
`;

const Textarea = styled.textarea`
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.875rem;
    min-height: 300px;
    font-family: inherit;
    white-space: pre-wrap;
    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
    padding: 0.75rem 1.5rem;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    ${props => props.$variant === 'primary' ? `
        background: #2563eb;
        color: white;
        border: none;
        &:hover:not(:disabled) {
            background: #1d4ed8;
        }
    ` : `
        background: white;
        color: #374151;
        border: 1px solid #d1d5db;
        &:hover:not(:disabled) {
            background: #f3f4f6;
        }
    `}
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ErrorMessage = styled.div`
    background-color: #feecea;
    color: #dc2626;
    padding: 0.75rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-size: 0.875rem;
    border: 1px solid #fcd4cd;
`;

const SuccessMessage = styled.div`
    background-color: #e9f7ef;
    color: #059669;
    padding: 0.75rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-size: 0.875rem;
    border: 1px solid #cfeedd;
`;

const Loading = styled.div`
    text-align: center;
    padding: 2rem;
    color: #6b7280;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 2rem;
    color: #6b7280;
`;

const ActionButton = styled.button`
    padding: 0.375rem 0.75rem;
    margin-right: 0.5rem;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    &:hover {
        background: #f3f4f6;
    }
    &.primary {
        background: #2563eb;
        color: white;
        border-color: #2563eb;
        &:hover {
            background: #1d4ed8;
        }
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 8px;
    padding: 2rem;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    width: 90%;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
    font-size: 1.5rem;
    color: #111827;
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6b7280;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    &:hover {
        background: #f3f4f6;
    }
`;

const EmailBody = styled.div`
    white-space: pre-wrap;
    font-family: inherit;
    line-height: 1.6;
    color: #374151;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
`;

const EmailMeta = styled.div`
    margin-bottom: 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    div {
        margin-bottom: 0.5rem;
    }
    strong {
        color: #374151;
    }
`;

// Helper to extract error message from unknown error type
const getErrorMessage = (err: unknown): string => {
    if (err instanceof Error) return err.message;
    if (
        err &&
        typeof err === 'object' &&
        'error' in err &&
        typeof (err as { error: unknown }).error === 'string'
    ) {
        return (err as { error: string }).error;
    }
    if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: unknown }).message === 'string'
    ) {
        return (err as { message: string }).message;
    }
    return 'Unknown error';
};

const PublisherCommunications: React.FC = () => {
    const { publisherId } = useParams<{ publisherId: string }>();
    const navigate = useNavigate();
    const [publisher, setPublisher] = useState<Publisher | null>(null);
    const [tracks, setTracks] = useState<TrackRecord[]>([]);
    const [communications, setCommunications] = useState<PublisherCommunication[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Email form state
    const [toEmail, setToEmail] = useState('');
    const [toName, setToName] = useState('');
    const [subject, setSubject] = useState('Music Licensing Opportunity - Norman Blagman Catalog');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [viewingEmail, setViewingEmail] = useState<PublisherCommunication | null>(null);
    const [replyingToId, setReplyingToId] = useState<number | undefined>(undefined);

    // Load publisher, tracks, and communications
    useEffect(() => {
        if (!publisherId) return;

        const loadData = async () => {
            setLoading(true);
            setError('');

            const id = parseInt(publisherId);

            try {
                // Load all data in parallel for better performance
                const [publisherData, tracksData, commsData] = await Promise.all([
                    crmService.getPublisher(id),
                    crmService.getPublisherTracks(id),
                    crmService.getPublisherCommunications(id).catch(() => ({ communications: [] }))
                ]);

                // Process publisher data
                setPublisher(publisherData);
                const primaryContact = publisherData.contacts?.[0];
                if (primaryContact) {
                    setToName(primaryContact.name || '');
                    setToEmail(primaryContact.email || '');
                }

                // Process tracks data
                const tracksList = tracksData.tracks || [];
                setTracks(tracksList);

                // Generate email template with track info
                const filteredTracks = tracksList.filter(Boolean);
                const contactName = primaryContact?.name || '[ContactName]';
                const template = generateEmailTemplate(contactName, filteredTracks);
                setBody(template);

                // Process communications data
                setCommunications(commsData.communications || []);
            } catch (err: unknown) {
                setError(getErrorMessage(err) || 'Failed to load publisher data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [publisherId]);

    const generateEmailTemplate = (contactName: string, tracks: TrackRecord[]): string => {
        const trackList = tracks.length > 0
            ? tracks.map(track => `${track.title} - ${track.artist}\n⸻`).join('\n').replace(/\n⸻$/, '')
            : '';

        return `Dear ${contactName},

I'm reaching out on behalf of WoodyBlagman.com Inc., representing the works of Norman Blagman, whose songs were recorded by artists including Elvis Presley, Baby Washington, Polly Bergen, Jimmy Dean, and Los Lobos, and spanning hundreds of titles across a 70-year career.

We are excited to request your authorization to include these songs in our catalog — songs for which you hold either the publishing rights or ownership of the master.

Titles we are interested in:

${trackList}

Additionally, please let us know if your catalog contains any other Norman Blagman–written songs. All licensing and promotion would be done with your approval, and your ownership rights would be fully respected.

Thank you for your time and consideration. I look forward to your response.

Best regards,
Scott Blagman
President, WoodyBlagman.com Inc.
woody@blagmanbros.com
(310) 433-1952`;
    };

    const handleSendEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!publisherId || !toEmail || !subject || !body) {
            setError('Please fill in all required fields');
            return;
        }

        setSending(true);
        setError('');
        setSuccess('');

        try {
            const emailData: SendPublisherEmailRequest = {
                to_email: toEmail,
                to_name: toName || undefined,
                subject,
                body,
                contact_id: publisher?.contacts?.[0]?.id,
                in_reply_to: replyingToId
            };

            const response = await crmService.sendPublisherEmail(parseInt(publisherId), emailData);
            
            if (response.success) {
                setSuccess('Email sent successfully!');
                // Clear reply tracking
                setReplyingToId(undefined);
                // Reload communications to show the new email
                const commsData = await crmService.getPublisherCommunications(parseInt(publisherId));
                setCommunications(commsData.communications);
                // Clear form after a delay
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            } else {
                setError(response.error || 'Failed to send email');
            }
        } catch (err: unknown) {
            setError(getErrorMessage(err) || 'Failed to send email');
        } finally {
            setSending(false);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return dateString;
        }
    };

    const handleViewEmail = (comm: PublisherCommunication) => {
        setViewingEmail(comm);
    };

    const handleReply = (comm: PublisherCommunication) => {
        if (comm.direction !== 'received') return;
        
        // Pre-fill the form with reply information
        setToEmail(comm.sender_email);
        
        // Set the in_reply_to ID to link this reply to the original communication
        setReplyingToId(comm.id);
        
        // Try to find the contact name from the publisher's contacts
        let contactName = '';
        if (publisher && publisher.contacts) {
            // First try to find by contact_id if available
            if (comm.contact_id) {
                const contact = publisher.contacts.find(c => c.id === comm.contact_id);
                if (contact && contact.name) {
                    contactName = contact.name;
                }
            }
            
            // If not found, try to find by email address
            if (!contactName) {
                const contact = publisher.contacts.find(c => 
                    c.email && c.email.toLowerCase() === comm.sender_email.toLowerCase()
                );
                if (contact && contact.name) {
                    contactName = contact.name;
                }
            }
        }
        
        // Fall back to recipient_name (which stores sender name for received emails)
        // Then fall back to email username if nothing found
        setToName(contactName || comm.recipient_name || comm.sender_email.split('@')[0]);
        setSubject(comm.subject.startsWith('Re:') ? comm.subject : `Re: ${comm.subject}`);
        
        // Create reply body with original message quoted
        const replyBody = `\n\n--- Original Message ---\nFrom: ${comm.sender_email}\nDate: ${formatDate(comm.received_at || comm.sent_at)}\nSubject: ${comm.subject}\n\n${comm.body}\n\n`;
        setBody(replyBody);
        
        // Scroll to the email form
        const formElement = document.getElementById('email-form');
        if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const closeModal = () => {
        setViewingEmail(null);
    };

    if (loading) {
        return (
            <Container>
                <Loading>Loading publisher communications...</Loading>
            </Container>
        );
    }

    if (!publisher) {
        return (
            <Container>
                <ErrorMessage>Publisher not found</ErrorMessage>
                <BackButton onClick={() => navigate('/admin/tracks')}>Back to Tracks</BackButton>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <BackButton onClick={() => navigate('/admin/tracks')}>← Back to Tracks</BackButton>
                <Title>Publisher Communications: {publisher.name}</Title>
            </Header>

            {error && (
                <ErrorMessage style={{ marginBottom: '1rem' }}>
                    {error}
                </ErrorMessage>
            )}

            {/* Publisher Information */}
            <Section>
                <SectionTitle>Publisher Information</SectionTitle>
                <PublisherInfo>
                    <InfoItem>
                        <label>Name</label>
                        <div>{publisher.name}</div>
                    </InfoItem>
                    {publisher.contacts && publisher.contacts.length > 0 && (
                        <>
                            <InfoItem>
                                <label>Primary Contact</label>
                                <div>{publisher.contacts[0].name || 'N/A'}</div>
                            </InfoItem>
                            <InfoItem>
                                <label>Email</label>
                                <div>{publisher.contacts[0].email || 'N/A'}</div>
                            </InfoItem>
                            <InfoItem>
                                <label>Phone</label>
                                <div>{publisher.contacts[0].phone || 'N/A'}</div>
                            </InfoItem>
                        </>
                    )}
                </PublisherInfo>
            </Section>

            {/* Tracks Section */}
            <Section>
                <SectionTitle>Tracks ({tracks.length})</SectionTitle>
                {tracks.length === 0 ? (
                    <EmptyState>No tracks found for this publisher</EmptyState>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <Th>Title</Th>
                                <Th>Artist</Th>
                                <Th>ISWC</Th>
                                <Th>ISRC</Th>
                                <Th>Notes</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {tracks.map(track => (
                                <tr key={track.id}>
                                    <Td>{track.title}</Td>
                                    <Td>{track.artist}</Td>
                                    <Td>{track.iswc || '-'}</Td>
                                    <Td>{track.isrc || '-'}</Td>
                                    <Td style={{ whiteSpace: 'pre-wrap', maxWidth: '300px' }}>{track.notes || '-'}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Section>

            {/* Email Form Section */}
            <Section id="email-form">
                <SectionTitle>Send Email</SectionTitle>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}
                <form onSubmit={handleSendEmail}>
                    <FormGroup>
                        <Label htmlFor="toName">Contact Name</Label>
                        <Input
                            id="toName"
                            type="text"
                            value={toName}
                            onChange={e => setToName(e.target.value)}
                            placeholder="Contact name"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="toEmail">Email Address *</Label>
                        <Input
                            id="toEmail"
                            type="email"
                            value={toEmail}
                            onChange={e => setToEmail(e.target.value)}
                            placeholder="publisher@example.com"
                            required
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                            id="subject"
                            type="text"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            required
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="body">Email Body *</Label>
                        <Textarea
                            id="body"
                            value={body}
                            onChange={e => setBody(e.target.value)}
                            required
                        />
                    </FormGroup>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <Button type="submit" $variant="primary" disabled={sending}>
                            {sending ? 'Sending...' : 'Send Email'}
                        </Button>
                        <Button type="button" onClick={() => {
                            const trackList = tracks.filter(Boolean);
                            const contactName = toName || publisher.contacts?.[0]?.name || '[ContactName]';
                            setBody(generateEmailTemplate(contactName, trackList));
                        }}>
                            Reset Template
                        </Button>
                    </div>
                </form>
            </Section>

            {/* Communication History */}
            <Section>
                <SectionTitle>Communication History ({communications.length})</SectionTitle>
                {communications.length === 0 ? (
                    <EmptyState>No emails sent or received yet</EmptyState>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <Th>Date</Th>
                                <Th>Direction</Th>
                                <Th>To/From</Th>
                                <Th>Subject</Th>
                                <Th>Sent By</Th>
                                <Th>Status</Th>
                                <Th>Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {communications.map(comm => {
                                const isReceived = comm.direction === 'received';
                                const date = isReceived ? comm.received_at : comm.sent_at;
                                const toFromEmail = isReceived ? comm.sender_email : comm.recipient_email;
                                const toFromName = isReceived ? null : comm.recipient_name;
                                
                                return (
                                    <tr 
                                        key={comm.id}
                                        style={{
                                            backgroundColor: isReceived ? '#f0f9ff' : 'transparent'
                                        }}
                                    >
                                        <Td>{formatDate(date)}</Td>
                                        <Td>
                                            <span style={{
                                                color: isReceived ? '#2563eb' : '#059669',
                                                fontWeight: 600,
                                                fontSize: '0.875rem'
                                            }}>
                                                {isReceived ? 'Received' : 'Sent'}
                                            </span>
                                        </Td>
                                        <Td>
                                            {isReceived ? (
                                                <span>
                                                    From: {toFromEmail}
                                                </span>
                                            ) : (
                                                <span>
                                                    {toFromName ? `${toFromName} <${toFromEmail}>` : toFromEmail}
                                                </span>
                                            )}
                                        </Td>
                                        <Td>{comm.subject}</Td>
                                        <Td>{isReceived ? 'System' : comm.sent_by}</Td>
                                        <Td>
                                            <span style={{
                                                color: comm.status === 'sent' || comm.status === 'received' ? '#059669' : comm.status === 'failed' ? '#dc2626' : '#6b7280',
                                                fontWeight: 500
                                            }}>
                                                {comm.status}
                                            </span>
                                        </Td>
                                        <Td>
                                            <ActionButton onClick={() => handleViewEmail(comm)}>
                                                View
                                            </ActionButton>
                                            {isReceived && (
                                                <ActionButton className="primary" onClick={() => handleReply(comm)}>
                                                    Reply
                                                </ActionButton>
                                            )}
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </Section>

            {/* Email View Modal */}
            {viewingEmail && (
                <ModalOverlay onClick={closeModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>Email Details</ModalTitle>
                            <CloseButton onClick={closeModal}>×</CloseButton>
                        </ModalHeader>
                        <EmailMeta>
                            <div><strong>From:</strong> {viewingEmail.sender_email}</div>
                            <div><strong>To:</strong> {viewingEmail.recipient_email}</div>
                            <div><strong>Subject:</strong> {viewingEmail.subject}</div>
                            <div><strong>Date:</strong> {formatDate(viewingEmail.received_at || viewingEmail.sent_at)}</div>
                            <div><strong>Direction:</strong> {viewingEmail.direction === 'received' ? 'Received' : 'Sent'}</div>
                        </EmailMeta>
                        <EmailBody>{viewingEmail.body}</EmailBody>
                        {viewingEmail.direction === 'received' && (
                            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
                                <ActionButton className="primary" onClick={() => {
                                    closeModal();
                                    handleReply(viewingEmail);
                                }}>
                                    Reply
                                </ActionButton>
                            </div>
                        )}
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
};

export default PublisherCommunications;

